/* ────────────────────────────────────────────────────────────────────────
 * <ContactActionButtons /> — premium drop-in Call + Email button pair.
 *
 * Wraps `useContactActions()` so callers don't have to wire the modal
 * + handlers themselves. Used by Customer Profile, Lead headers,
 * Inquiry Drawer, Quick Action bars, CRM cards, and any other surface
 * that surfaces a contact's phone/email next to action chrome.
 *
 * Visual behaviour:
 *   • Both buttons share the same MUI outlined-button look so any
 *     surface gets a consistent action row.
 *   • Email button shows a small spinner endAdornment while it's
 *     first-loading email accounts — prevents the modal from flashing
 *     open empty.
 *   • Missing phone / email → button is `disabled` AND wears a Tooltip
 *     explaining why ("No phone number available" / "No email address
 *     available") so users know which field to fill in.
 * ──────────────────────────────────────────────────────────────────── */

import React from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import PhoneIcon from "@mui/icons-material/PhoneOutlined";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import { useContactActions, EmailActionParams } from "./useContactActions";

interface Props {
  /** Phone number — any format. Empty string / null disables Call. */
  phone?: string | null;
  /** Email address. Empty / null disables Email. */
  email?: string | null;
  /** Customer / lead name — header context only. */
  name?: string | null;
  /** Optional email thread id when the surface is thread-aware
   *  (Lead Detail, Inquiry Drawer). Forwarded to the modal so the
   *  send is attributed to the right thread. */
  threadId?: number | null;
  /** CRM linkage — forwarded onto the persisted email row so the
   *  Client Profile / Lead View "Email History" lists are single
   *  indexed queries. Use customerId on Client Profile and leadId on
   *  lead-aware surfaces; both can be supplied together. */
  customerId?: number | null;
  leadId?: number | null;
  /** Pre-fill the body — typically left undefined; the composer
   *  toolbar lets reps pick a template after the modal opens. */
  initialBody?: string;
  /** Fires after a successful send so the host can refresh its
   *  activity timeline / message list. */
  onSent?: () => void;
  /** Render as small icon-only buttons instead of labelled buttons.
   *  Use on compact surfaces (table rows, dense cards). */
  variant?: "buttons" | "icons";
  /** MUI Button size — propagates through to both buttons. */
  size?: "small" | "medium";
  /** Override the default mini-row layout (e.g. add gap, justify). */
  sx?: any;
}

/**
 * Renders the buttons AND the email modal in a single tree. Mount
 * anywhere inside a NotifierProvider.
 */
export default function ContactActionButtons({
  phone, email, name, threadId, customerId, leadId, initialBody, onSent,
  variant = "buttons", size = "small", sx,
}: Props) {
  const { handleCallAction, handleEmailAction, emailLoading, modalElement } = useContactActions();

  const phoneDisabled = !phone || !phone.trim();
  const emailDisabled = !email || !email.trim();

  const emailParams: EmailActionParams = {
    email: email || null, name: name || null,
    threadId, customerId, leadId,
    initialBody, onSent,
  };

  const callTooltip  = phoneDisabled ? "No phone number available" : `Call ${phone}`;
  const emailTooltip = emailDisabled ? "No email address available" : `Email ${email}`;

  if (variant === "icons") {
    return (
      <>
        <Box sx={{ display: "inline-flex", gap: 0.75, ...sx }}>
          {/* Tooltip on a disabled button needs a wrapping span — MUI's
              rule: disabled elements don't fire mouseenter, so the tip
              never renders without the span. */}
          <Tooltip title={callTooltip}>
            <span>
              <IconButton
                size={size}
                disabled={phoneDisabled}
                onClick={() => handleCallAction(phone)}
                sx={{ border: "1px solid #ddd" }}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={emailTooltip}>
            <span>
              <IconButton
                size={size}
                disabled={emailDisabled || emailLoading}
                onClick={() => handleEmailAction(emailParams)}
                sx={{ border: "1px solid #ddd" }}
              >
                {emailLoading
                  ? <CircularProgress size={14} sx={{ color: "#94a3b8" }} />
                  : <EmailIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        {modalElement}
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "inline-flex", gap: 1, ...sx }}>
        <Tooltip title={callTooltip}>
          <span>
            <Button
              variant="outlined"
              size={size}
              startIcon={<PhoneIcon />}
              disabled={phoneDisabled}
              onClick={() => handleCallAction(phone)}
              sx={{ textTransform: "none", borderRadius: 1.5 }}
            >
              Call
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={emailTooltip}>
          <span>
            <Button
              variant="outlined"
              size={size}
              startIcon={emailLoading
                ? <CircularProgress size={14} sx={{ color: "currentColor" }} />
                : <EmailIcon />}
              disabled={emailDisabled || emailLoading}
              onClick={() => handleEmailAction(emailParams)}
              sx={{ textTransform: "none", borderRadius: 1.5 }}
            >
              Email
            </Button>
          </span>
        </Tooltip>
      </Box>
      {modalElement}
    </>
  );
}
